import React, { useEffect, useState } from "react";

/**
 * ScrollProgressRing
 * A thin cyan SVG arc pinned to the bottom-right that fills
 * as the user scrolls the page. Subtle, premium, non-intrusive.
 */
export default function ScrollProgressRing() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = doc.scrollTop || document.body.scrollTop;
      const height = doc.scrollHeight - doc.clientHeight;
      const pct = height > 0 ? Math.min(1, Math.max(0, scrolled / height)) : 0;
      setProgress(pct);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const R = 22;
  const C = 2 * Math.PI * R;
  const dash = C * progress;

  const scrollTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <button
      type="button"
      onClick={scrollTop}
      aria-label="Scroll to top"
      data-testid="scroll-progress-ring"
      className="fixed z-[1150] bottom-5 right-5 md:bottom-7 md:right-7 w-[52px] h-[52px] rounded-full bg-white/85 backdrop-blur-md border border-white/60 shadow-[0_10px_28px_-14px_rgba(2,50,110,0.4)] hover:shadow-[0_14px_34px_-14px_rgba(6,182,212,0.55)] hover:bg-white transition-all duration-300 flex items-center justify-center group"
      style={{ opacity: progress > 0.02 ? 1 : 0, pointerEvents: progress > 0.02 ? "auto" : "none" }}
    >
      <svg width="52" height="52" viewBox="0 0 52 52" className="-rotate-90">
        {/* Track */}
        <circle
          cx="26"
          cy="26"
          r={R}
          fill="none"
          stroke="rgba(6,102,182,0.10)"
          strokeWidth="2"
        />
        {/* Progress arc */}
        <circle
          cx="26"
          cy="26"
          r={R}
          fill="none"
          stroke="url(#spr-grad)"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={C - dash}
          style={{ transition: "stroke-dashoffset 120ms linear" }}
        />
        <defs>
          <linearGradient id="spr-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#22D3EE" />
            <stop offset="100%" stopColor="#0EA5E9" />
          </linearGradient>
        </defs>
      </svg>
      {/* Center arrow (visible after scrolling meaningfully) */}
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        className="absolute text-cyan-600 group-hover:-translate-y-0.5 transition-transform"
        aria-hidden="true"
      >
        <path
          d="M6 1.5v9M2 5l4-4 4 4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
